import React, { useEffect, useState } from "react";
import { rsvpMeeting, getAllMeetings } from '../../../controllers/speed-meeting/speed-meeting';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Collapse,
  IconButton,
  Chip,
  Box,
  Typography,
  Grow,
  Fade,
  Skeleton,
  CircularProgress,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";

const SpeedMeetingGroupsTable = ({ token, user, setName, setGroup }) => {

  const [speedMeetings, setSpeedMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [rsvpLoading, setRsvpLoading] = useState(false);

  useEffect(() => {
    const fetchMeetings = async () => {
      try {
        const data = await getAllMeetings({ token });
        setSpeedMeetings(data.speed_meetings);
      } catch (error) {
        console.error('Error fetching meetings:', error);
        setSpeedMeetings([]);
      }
      setLoading(false);
    };
    
    if (token) {
      fetchMeetings();
    } else {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    console.log('Speed meetings updated:', speedMeetings);
  }, [speedMeetings]);

  const handleExpand = (idx) => {
    setExpandedGroup(expandedGroup === idx ? null : idx);
  };

  const isJoinEnabled = (startTime) => {
    if (!startTime) return false;
    const now = new Date();
    const sessionTime = new Date(startTime);
    const diffMs = Math.abs(now - sessionTime);
    return diffMs <= 2 * 60 * 1000; // 2 minutes in milliseconds
  };

  const handleRSVP = async (session, meetingTitle) => {
    if (hasUserRSVPd(session, meetingTitle)) {
      alert('You have already RSVP\'d to this session');
      return;
    }

    try {
      await rsvpMeeting({
        token: user.token,
        title: meetingTitle,
        start_time: session.start_time,
        user_id: user.id,
        avatar: user.avatar
      });
      
      alert('RSVP successful!');
      // Refresh meetings to show updated RSVP
      await refreshMeetings();
    } catch (error) {
      console.error('RSVP failed:', error);
      alert(`RSVP failed: ${error.message}`);
    }
  };

  // Check if user has RSVP'd (server or client-side)
  const hasUserRSVPd = (session, meetingTitle) => {
    // Only check server-side RSVPs
    return session.rsvps && session.rsvps.some(rsvp => rsvp.id === user.id);
  };

  // Get combined RSVPs for display
    const refreshMeetings = async () => {
    try {
      const data = await getAllMeetings({token: user.token});
      setSpeedMeetings(data.speed_meetings);
    } catch (error) {
      console.error('Failed to refresh meetings:', error);
    }
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ boxShadow: 6, borderRadius: 3, mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: '#f5f5f5' }}>
              <TableCell />
              <TableCell sx={{ fontWeight: 700, fontSize: 18 }}>Speed Meetings</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              // Loading skeleton
              Array.from({ length: 3 }).map((_, idx) => (
                <TableRow key={idx}>
                  <TableCell>
                    <Skeleton variant="circular" width={24} height={24} />
                  </TableCell>
                  <TableCell>
                    <Skeleton variant="text" width="80%" height={24} />
                  </TableCell>
                </TableRow>
              ))
            ) : speedMeetings?.length > 0 ? (
              speedMeetings.map((speed_meeting, idx) => (
                <React.Fragment key={idx}>
                  <Grow in timeout={400}>
                    <TableRow
                      hover
                      sx={{
                        cursor: 'pointer',
                        background: expandedGroup === idx ? '#e3f2fd' : 'inherit',
                        transition: 'background 0.3s',
                      }}
                    >
                      <TableCell>
                        <IconButton
                          aria-label="expand row"
                          size="small"
                          onClick={() => handleExpand(idx)}
                        >
                          {expandedGroup === idx ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 600, fontSize: 16 }}
                        onClick={() => handleExpand(idx)}
                      >
                        {speed_meeting.title}
                        <Chip 
                          label={`${speed_meeting.sessions?.filter(session => new Date(session.start_time) >= new Date()).length || 0} sessions`} 
                          size="small" 
                          sx={{ ml: 1 }} 
                        />
                      </TableCell>
                    </TableRow>
                  </Grow>
                <TableRow>
                  <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={3}>
                    <Collapse in={expandedGroup === idx} timeout={500} unmountOnExit>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ background: '#f0f4c3' }}>
                            <TableCell />
                            <TableCell sx={{ pl: 4, fontWeight: 600 }}>Date & Time</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>RSVPs</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {speed_meeting.sessions && speed_meeting.sessions.length > 0 ? (
                            speed_meeting.sessions
                              .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                              .filter((session) => new Date(session.start_time) >= new Date()) // Hide past sessions
                              .length > 0 ? (
                              speed_meeting.sessions
                                .sort((a, b) => new Date(a.start_time) - new Date(b.start_time))
                                .filter((session) => new Date(session.start_time) >= new Date())
                                .map((session, sIdx) => (
                                  <Fade in timeout={400 + sIdx * 100} key={sIdx}>
                                    <TableRow
                                      sx={{
                                        background: '#fafafa',
                                        transition: 'background 0.3s',
                                        '&:hover': { background: '#e1f5fe' },
                                      }}
                                    >
                                      <TableCell />
                                      <TableCell sx={{ pl: 4 }}>
                                        {new Date(session.start_time).toLocaleString()}
                                      </TableCell>
                                      <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                          {session.rsvps && session.rsvps.length > 0 ? (
                                            <>
                                              <Box display="flex" sx={{ mr: 1 }}>
                                                {session.rsvps.slice(0, 3).map((user, userIdx) => (
                                                  <Box
                                                    key={userIdx}
                                                    sx={{
                                                      ml: userIdx > 0 ? -1 : 0,
                                                      zIndex: 3 - userIdx,
                                                    }}
                                                  >
                                                    <img
                                                      src={user.avatar || 'https://via.placeholder.com/24'}
                                                      alt={user.name || `User ${user.id}`}
                                                      title={user.name || `User ${user.id}`}
                                                      style={{
                                                        width: 24,
                                                        height: 24,
                                                        borderRadius: "50%",
                                                        objectFit: "cover",
                                                        border: "1px solid #fff",
                                                        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
                                                      }}
                                                    />
                                                  </Box>
                                                ))}
                                              </Box>
                                              {session.rsvps.length > 3 && (
                                                <Chip
                                                  label={`+${session.rsvps.length - 3}`}
                                                  size="small"
                                                  sx={{ fontSize: '0.7rem', height: 20 }}
                                                />
                                              )}
                                              <Chip
                                                label={session.rsvps.length}
                                                size="small"
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                              />
                                            </>
                                          ) : (
                                            <Typography variant="caption" color="text.secondary">
                                              No RSVPs
                                            </Typography>
                                          )}
                                        </Box>
                                      </TableCell>
                                      <TableCell>
                                        <Button
                                          variant="contained"
                                          size="small"
                                          disabled={!isJoinEnabled(session.start_time)}
                                          onClick={() => {
                                            setGroup(speed_meeting.title)
                                            setName(user.id)
                                          }}
                                          sx={{ fontWeight: 600 }}
                                        >
                                          Join
                                        </Button>
                                        <Button
                                          variant="outlined"
                                          size="small"
                                          sx={{ ml: 1, fontWeight: 600 }}
                                          disabled={
                                            rsvpLoading ||
                                            !user || 
                                            !user.id || 
                                            hasUserRSVPd(session, user.id)
                                          }
                                          onClick={() =>
                                            handleRSVP(session, speed_meeting.title)
                                          }
                                        >
                                          {rsvpLoading ? (
                                            <CircularProgress size={16} />
                                          ) : hasUserRSVPd(session, user?.id) ? 'RSVP\'d' : 'RSVP'}
                                        </Button>
                                      </TableCell>
                                    </TableRow>
                                  </Fade>
                                ))
                            ) : (
                              <TableRow>
                                <TableCell colSpan={4}>No upcoming sessions available</TableCell>
                              </TableRow>
                            )
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4}>No sessions available</TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={2}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                    <Typography>No speed meetings available</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

export default SpeedMeetingGroupsTable;